// @LTG-ID: contact-idempotency
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: ADR-027 §3 (KGOB dual-write idempotency contract)
// @CHANGED: 2026-05 session_006 (CHG-0083-M-A2)
// @OWNER: web
//
// CHG-0083-M-A2 — Idempotency key generator + validator.
//
// Format: `ltg-contact-<uuid>` (where <uuid> is uuid-v4 36-char hex).
// Used as:
//   1. Local frontend correlation key (shown as "Reference: …" to the user).
//   2. `X-Request-ID` HTTP header on POST /api/contact (server-side trace).
//   3. `attribution.kgob_idempotency_key` field in the request body
//      (for CHG-0084's KGOB forwarding-layer dedupe — see ADR-027 §3).
//
// Note: per ORCH-0087 A.3 #3, this is the canonical "reference code" format
// for M-A2 onwards (overriding v4 source's local `LTG-{CAT}-{TS}-{RANDOM}`
// pattern which is mock-only). Idempotency contract reuses this key on
// retries so the backend (and downstream KGOB layer) can dedupe.

const ID_PREFIX = "ltg-contact-";

// Browser-native uuid-v4 if available (modern Chromium/Firefox/Safari support
// crypto.randomUUID since 2021). Fallback to a non-crypto-grade composite
// that still satisfies the validator regex.
function uuidv4() {
    if (
        typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function"
    ) {
        return crypto.randomUUID();
    }
    // Fallback: combine high-res time + Math.random. Not RFC 4122 compliant
    // but still ~10^12 entropy — enough for a single user's submission stream.
    const a = Date.now().toString(16).padStart(12, "0");
    const b = Math.floor(Math.random() * 0xffffffff)
        .toString(16)
        .padStart(8, "0");
    const c = Math.floor(Math.random() * 0xffffffff)
        .toString(16)
        .padStart(8, "0");
    return `${a.slice(0, 8)}-${a.slice(8, 12)}-4${b.slice(1, 4)}-a${c.slice(1, 4)}-${b}${c.slice(0, 4)}`;
}

export function generateIdempotencyKey() {
    return ID_PREFIX + uuidv4();
}

// Validator regex per ORCH-0087 point 6: id format must match
// `ltg-contact-<uuid>` (allow lower-case hex + dashes, 8+ chars after prefix).
const ID_PATTERN = /^ltg-contact-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function validateIdempotencyKey(s) {
    return typeof s === "string" && ID_PATTERN.test(s);
}

export default generateIdempotencyKey;
